import React, { useEffect } from 'react';

import { useAuthService } from '@bessemercws/auth';
import { Header, HeaderProps } from '@bessemercws/components';
import { isUserProfileType, OnDocumentUrlReadyDocument, User, useSubscription } from '@bessemercws/graphql';
import { useClientContext } from '@bessemercws/providers';
import { useSnackbarContext } from '@bessemercws/utils';
import { decode } from 'base-64';
import { useNavigate, useParams, useLocation, matchPath } from 'react-router-dom';

import { SECURE_MAIL_URI } from '#/constants';
import { fetchDownloadFromUrl } from '#/hooks';
import { useLogout } from '#/hooks/logout/useLogout';
import { useGraphqlService } from '#/services';
import { getHeaderNavTabs } from './getHeaderNavTabs';
import { HeaderContainer } from './Header.styles';
import { useAdditionalServices } from './hooks';
import { headerLinksByUserProfileType } from './UserProfileTypeHeaderLinkConfig';

export const SimpleHeader = (props: HeaderProps) => {
  /** Hooks */
  const navigate = useNavigate();
  const authService = useAuthService();
  const { clientId } = useParams();
  const { pathname } = useLocation();
  const { logoutCallback } = useLogout();

  const { buildAdditionalServices, buildAdditionalServicesTab, useAdditionalServicesQuery } = useAdditionalServices();
  const [additionalServicesUser] = useAdditionalServicesQuery();
  const {
    isAdvisor,
    userId,
    setSelectedClient,
    hasConsolidatedReporting,
    authSessionToken,
    idToken,
    subscriptionChannel,
    userProfileType,
  } = useClientContext();
  const { getHeaderData, useGetMyTeamListImages, useAdvisorListFragment } = useGraphqlService();

  const { handleCloseSnackbar } = useSnackbarContext();

  /** Data */
  const decodedClientId = clientId && decode(clientId);
  const { data, loading: loadingHeaderData } = getHeaderData(decodedClientId);
  const { loading: myTeamImagesLoading } = useGetMyTeamListImages(decodedClientId);
  const { advisors, isComplete: hasCompleteAdvisorList } = useAdvisorListFragment(
    isAdvisor ? decodedClientId : userId?.toUpperCase(),
  );

  const { data: downloadUrls } = useSubscription(OnDocumentUrlReadyDocument, {
    variables: {
      userId: data?.userId,
      sessionId: data?.user_id_hash,
      channel: subscriptionChannel,
    },
    skip: !data?.userId || !data?.user_id_hash || !subscriptionChannel,
  });

  useEffect(() => {
    if (downloadUrls?.onDocumentUrlReady) {
      const url = downloadUrls.onDocumentUrlReady.url;
      fetchDownloadFromUrl(url, idToken);
      handleCloseSnackbar();
    }
  }, [downloadUrls?.onDocumentUrlReady]);

  const additionalServices = buildAdditionalServices(additionalServicesUser as User, hasConsolidatedReporting);
  const isTabDisabled = Boolean(!data || data.isNewUser);

  const userProfileTypeLinkConfig = isUserProfileType(userProfileType)
      ? headerLinksByUserProfileType[userProfileType]
      : undefined;

  const isUnknownUserProfileType = !userProfileType;

  const headerNavTabs = getHeaderNavTabs({
        isTabDisabled,
        additionalServices,
        buildAdditionalServicesTab,
        userProfileTypeLinkConfig,
        isUnknownUserProfileType,
      });

  const handleTabNavigate = (_event: React.SyntheticEvent<Element, Event>, newValue?: string) => {
    if (newValue === undefined) return;
    if (newValue === '/account') {
      if (clientId) {
        navigate(`/advisors/${clientId}`);
      } else {
        navigate('/');
      }
    } else {
      navigate(newValue);
      if (clientId) {
        navigate(`/advisors/${clientId}/${newValue}`);
      } else {
        navigate(newValue);
      }
    }
  };

  const handleLogoClick = async () => {
    if (clientId && userProfileTypeLinkConfig) {
      navigate(`/advisors/${clientId}/${userProfileTypeLinkConfig.homeLink}`);
    } else if (clientId) {
      navigate(`/advisors/${clientId}`);
    } else if (userProfileTypeLinkConfig) {
      navigate(userProfileTypeLinkConfig.homeLink);
    } else {
      navigate('/');
    }
  };

  const handleProfileClick = async () => {
    if (clientId) {
      navigate(`/advisors/${clientId}/preferences`);
    } else {
      navigate('/preferences/communications');
    }
  };

  const handleMessagesClick = () => {
    window.open(`${SECURE_MAIL_URI}${authSessionToken}`, '_blank');
  };

  const handleLogOut = async () => {
    if (isAdvisor) {
      setSelectedClient(undefined);
      authService.signOut(() => navigate('/advisors'));
    } else {
      authService.signOut(() => {
        logoutCallback();
      });
    }
  };

  const messages = {
    hasMessages: (additionalServicesUser as User)?.additionalServices?.messages ?? false,
    onMessagesClick: () => handleMessagesClick(),
  };

  const pathToMatch = isAdvisor ? '/advisors/:clientId/:tabName/*' : '/:tabName/*';
  const pathMatch = matchPath(pathToMatch, pathname);
  const tabName = pathMatch?.params.tabName;
  const selectedTab = tabName && tabName !== 'account' ? tabName : '';

  return (
    <>
      <HeaderContainer>
        <div className="ghost-header"></div>
        <Header
          {...props}
          data-testid="bui-header"
          name={data?.preferences?.userProfile?.name ?? ''}
          headerNavTabs={headerNavTabs}
          onNavigate={handleTabNavigate}
          currentTab={selectedTab}
          onLogout={handleLogOut}
          onLogoClick={handleLogoClick}
          onProfileClick={handleProfileClick}
          notifications={{
            items: [],
            loading: loadingHeaderData,
          }}
          myTeam={{
            items: advisors || [],
            loading: !hasCompleteAdvisorList,
            imagesLoading: myTeamImagesLoading,
          }}
          messages={messages}
        />
      </HeaderContainer>
    </>
  );
};

export default SimpleHeader;
